import React, { useState, useEffect, useCallback } from 'react';
import { getAdminGenerationJobs } from '../../api';
import { Link, useSearch } from '@tanstack/react-router';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';

// Helper for status badge styling (semantic tokens)
const getStatusClass = (status) => {
    switch (status) {
        case 'SUCCESS':
            return 'bg-success-soft text-success-soft-foreground border border-success-border';
        case 'FAILED':
            return 'bg-danger-soft text-danger-soft-foreground border border-danger-border';
        case 'GENERATING_IMAGE':
        case 'GENERATING_3D':
        case 'PROCESSING':
            return 'bg-info-soft text-info-soft-foreground border border-info-border';
        case 'PENDING':
        case 'IMAGE_READY':
            return 'bg-warning-soft text-warning-soft-foreground border border-warning-border';
        default:
            return 'bg-background-soft text-muted-foreground border border-border';
    }
};

const Admin3DJobsPage = () => {
    const [jobs, setJobs] = useState([]);
    const [totalJobs, setTotalJobs] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user_id: userIdFilter, username: usernameFilter } = useSearch({ });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const skip = (currentPage - 1) * pageSize;
            const response = await getAdminGenerationJobs(userIdFilter, pageSize, skip);
            setJobs(response.jobs || []);
            setTotalJobs(response.total || 0);
        } catch (err) {
            console.error("Failed to fetch generation jobs:", err);
            setError(err.message || 'Failed to load jobs.');
        } finally {
            setLoading(false);
        }
    }, [userIdFilter, currentPage, pageSize]);
    
    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    useEffect(() => {
        setCurrentPage(1);
    }, [userIdFilter]);

    const totalPages = Math.ceil(totalJobs / pageSize);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const crumbs = [
        { label: 'Admin', to: '/app/admin' },
        { label: '3D Jobs' },
    ];
    if (userIdFilter) {
        crumbs.splice(1, 0, { label: 'Users', to: '/app/admin/users' });
        crumbs[2].label = `3D Jobs for ${usernameFilter || userIdFilter}`;
    }

    return (
        <div className="text-foreground">
            <Breadcrumbs crumbs={crumbs} />

            {loading && <div className="text-foreground">Loading jobs...</div>}
            {error && <div className="text-danger">Error: {error}</div>}

            {!loading && !error && (
                <>
                    <h1 className="text-2xl font-semibold mb-6">
                        {userIdFilter ? `3D Jobs for ${usernameFilter || userIdFilter}` : 'All 3D Jobs'}
                    </h1>
            
            {userIdFilter && (
                 <div className="mb-4">
                    <Link 
                        to="/app/admin/generation-jobs" 
                        className="text-sm text-accent hover:underline hover:text-accent/90"
                    >
                        Show all 3D jobs
                    </Link>
                 </div>
             )}
            
            <div className="bg-background-elevated border border-border shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr className="bg-background-soft text-left text-muted-foreground uppercase text-sm">
                            <th className="px-5 py-3 border-b-2 border-border">Job ID</th>
                            <th className="px-5 py-3 border-b-2 border-border">Name</th>
                            <th className="px-5 py-3 border-b-2 border-border">Type</th>
                            <th className="px-5 py-3 border-b-2 border-border">User ID</th>
                            <th className="px-5 py-3 border-b-2 border-border">Status</th>
                            <th className="px-5 py-3 border-b-2 border-border">Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((job) => (
                            <tr 
                                key={job.id} 
                                className="hover:bg-background-soft cursor-pointer"
                                // onClick={() => navigate({ to: '/app/admin/generation-jobs/$jobId', params: { jobId: job.id } })} // Disable navigation for now
                            >
                                <td className="px-5 py-4 border-b border-border text-sm text-accent">
                                    <code>{job.id.substring(0, 8)}...</code>
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">
                                    {job.name || 'Untitled'}
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm">
                                    {job.generation_type}
                                </td>
                                 <td className="px-5 py-4 border-b border-border text-sm">
                                     <Link 
                                         to="/app/admin/users"
                                         search={{ user_id: job.user_id, username: job.username }} 
                                         className="text-accent hover:underline hover:text-accent/90"
                                         title={`View user: ${job.username || job.user_id}`}
                                         onClick={(e) => e.stopPropagation()}
                                     >
                                         {job.username ? job.username : <code>{job.user_id.substring(0, 8)}...</code>}
                                     </Link>
                                </td>
                          <td className="px-5 py-4 border-b border-border text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusClass(job.status)}`}>
                                        {job.status}
                                    </span>
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm whitespace-nowrap">
                                    {new Date(job.created_at).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {jobs.length === 0 && !loading && (
                     <div className="p-4 text-center text-muted-foreground">
                        No 3D jobs found{userIdFilter ? ` for user ${usernameFilter || userIdFilter}` : ''}.
                    </div>
                )}
            </div>
            
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    paginate={handlePageChange}
                />
            )}
        </>
    )}
        </div>
    );
};

export default Admin3DJobsPage;
